import { apiClient } from '@/sdk/shared/apiClient';
import { isJwtExpired } from './utils';
import type {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
} from './types';

// Cookie helper functions
const getCookie = (name: string): string | undefined => {
  if (typeof document === 'undefined') return undefined;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift();
  return undefined;
};

const setCookie = (name: string, value: string, days: number = 7): void => {
  if (typeof document === 'undefined') return;
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  const isSecure = typeof window !== 'undefined' && window.location.protocol === 'https:';
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax${isSecure ? ';Secure' : ''}`;
};

const deleteCookie = (name: string): void => {
  if (typeof document === 'undefined') return;
  const isSecure = typeof window !== 'undefined' && window.location.protocol === 'https:';
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;SameSite=Lax${isSecure ? ';Secure' : ''}`;
  document.cookie = `${name}=;max-age=0;path=/;`;
};

export const authApi = {
  login: async (data: LoginRequest): Promise<LoginResponse> => {
    const response = await apiClient.post<LoginResponse>('/auth/login', data);
    const role = response.userType === 'client' ? response.client?.role : response.user?.role;
    // For users, use roleSlug (admin/super_admin); for clients, use role
    const roleForCookie = response.userType === 'user' ? response.user?.roleSlug : role;
    if (response.token) {
      apiClient.setToken(response.token, roleForCookie || role || 'user');
    }

    // Store user data in cookies for AuthContext
    const authUser = response.userType === 'client' ? response.client : response.user;
    if (authUser && response.userType) {
      setCookie('auth_user', encodeURIComponent(JSON.stringify(authUser)));
      setCookie('auth_user_type', response.userType);
      if (authUser.tenant?.slug) {
        setCookie('tenant_slug', authUser.tenant.slug);
      }
      if (authUser.tenant?.id) {
        setCookie('tenant_id', authUser.tenant.id);
      }

      if (response.userType === 'client' && response.client) {
        const c = response.client;
        const termsAccepted = Boolean(
          c.termsAndConditionsAccepted ||
          c.terms_and_conditions_accepted ||
          c.termsAndConditions ||
          (c as any).terms_and_conditions
        );
        const hasMissingFields = Boolean(response.accessGate?.missingFields && response.accessGate.missingFields.length > 0);
        const hasAllFields = Boolean(
          (c.firstName || (c as any).first_name) &&
          (c.lastName || (c as any).last_name) &&
          (c.dateOfBirth || (c as any).date_of_birth || (c as any).dob) &&
          c.city &&
          c.country &&
          termsAccepted
        );
        if ((response.accessGate?.allowed || hasAllFields) && !hasMissingFields && termsAccepted) {
          setCookie('client_access_gate', 'complete');
        } else {
          setCookie('client_access_gate', 'incomplete');
        }
      }
    }

    return response;
  },

  loginWithGoogle: async (idToken: string, tenantSlug?: string, extraData?: Record<string, any>): Promise<LoginResponse> => {
    const response = await apiClient.post<LoginResponse>('/auth/google', { idToken, tenantSlug, ...extraData });
    if (response.isNewUser) {
      return response;
    }
    const role = response.userType === 'client' ? response.client?.role : response.user?.role;
    const roleForCookie = response.userType === 'user' ? response.user?.roleSlug : role;
    if (response.token) {
      apiClient.setToken(response.token, roleForCookie || role || 'user');
    }

    // Store user data in cookies for AuthContext
    const authUser = response.userType === 'client' ? response.client : response.user;
    if (authUser && response.userType) {
      setCookie('auth_user', encodeURIComponent(JSON.stringify(authUser)));
      setCookie('auth_user_type', response.userType);
      if (authUser.tenant?.slug) {
        setCookie('tenant_slug', authUser.tenant.slug);
      }
      if (authUser.tenant?.id) {
        setCookie('tenant_id', authUser.tenant.id);
      }

      if (response.userType === 'client' && response.client) {
        const c = response.client;
        const termsAccepted = Boolean(
          c.termsAndConditionsAccepted ||
          c.terms_and_conditions_accepted ||
          c.termsAndConditions ||
          (c as any).terms_and_conditions
        );
        const hasMissingFields = Boolean(response.accessGate?.missingFields && response.accessGate.missingFields.length > 0);
        const hasAllFields = Boolean(
          (c.firstName || (c as any).first_name) &&
          (c.lastName || (c as any).last_name) &&
          (c.dateOfBirth || (c as any).date_of_birth || (c as any).dob) &&
          c.city &&
          c.country &&
          termsAccepted
        );
        if ((response.accessGate?.allowed || hasAllFields) && !hasMissingFields && termsAccepted) {
          setCookie('client_access_gate', 'complete');
        } else {
          setCookie('client_access_gate', 'incomplete');
        }
      }
    }

    return response;
  },

  register: async (data: RegisterRequest): Promise<RegisterResponse> => {
    const response = await apiClient.post<RegisterResponse>('/auth/register', data);
    apiClient.setToken(response.token, response.user.role);

    // Store user data in cookies for AuthContext (registration creates a client)
    const cookieUser = {
      ...response.user,
      emailVerified: false
    };
    setCookie('auth_user', encodeURIComponent(JSON.stringify(cookieUser)));
    setCookie('auth_user_type', 'client');

    return response;
  },

  verifyEmail: async (token: string): Promise<any> => {
    return await apiClient.post<any>('/auth/verify-email', { token });
  },

  resendVerification: async (email?: string): Promise<any> => {
    if (email) {
      return await apiClient.post<any>('/auth/resend-verification', { email });
    }
    return await apiClient.post<any>('/client/email/resend-verification', {});
  },

  getClientAccessStatus: async (): Promise<any> => {
    return await apiClient.get<any>('/client/access-status');
  },

  completeRegistration: async (data: any): Promise<any> => {
    const isImpersonated =
      typeof window !== 'undefined' &&
      Boolean(sessionStorage.getItem('force_auth_token') || sessionStorage.getItem('force_auth_user'));

    const response = await apiClient.post<any>('/client/registration/complete', data);
    if (response?.token && response?.client) {
      if (isImpersonated) {
        // Tab-isolated impersonation session: update sessionStorage ONLY so staff cookies are untouched!
        sessionStorage.setItem('force_auth_token', response.token);
        sessionStorage.setItem('force_auth_user', JSON.stringify(response.client));
        sessionStorage.setItem('force_auth_user_type', 'client');
      } else {
        apiClient.setToken(response.token, response.client.role || 'client');
        setCookie('auth_user', encodeURIComponent(JSON.stringify(response.client)));
        setCookie('auth_user_type', 'client');
        if (response.client.tenant?.slug) {
          setCookie('tenant_slug', response.client.tenant.slug);
        }
        if (response.client.tenant?.id) {
          setCookie('tenant_id', response.client.tenant.id);
        }
      }
    }
    return response;
  },

  getMe: async (): Promise<any> => {
    return await apiClient.get<any>('/auth/me');
  },

  forgotPassword: async (data: ForgotPasswordRequest): Promise<ForgotPasswordResponse> => {
    return await apiClient.post<ForgotPasswordResponse>('/auth/forgot-password', data);
  },

  resetPassword: async (data: ResetPasswordRequest): Promise<ResetPasswordResponse> => {
    return await apiClient.post<ResetPasswordResponse>('/auth/reset-password', data);
  },

  getTenantTerms: async (slug: string): Promise<{ pdfUrl: string }> => {
    return await apiClient.get<{ pdfUrl: string }>(`/auth/tenant/${slug}/terms`);
  },

  getReferralDetails: async (code: string): Promise<{ success: boolean; node_id: string; tenant_id: string; tenant_name: string; tenant_slug: string }> => {
    return await apiClient.get<any>(`/auth/referral/${code}`);
  },

  logout: (): void => {
    if (typeof window !== 'undefined' && sessionStorage.getItem('force_auth_token')) {
      sessionStorage.removeItem('force_auth_token');
      sessionStorage.removeItem('force_auth_user');
      sessionStorage.removeItem('force_auth_user_type');
      sessionStorage.removeItem('force_auth_session_id');
      sessionStorage.removeItem('force_auth_admin');
      sessionStorage.removeItem('force_auth_started_at');
      deleteCookie('force_auth');
      deleteCookie('client_access_gate');
      return;
    }
    apiClient.clearToken();
    deleteCookie('auth_user');
    deleteCookie('auth_user_type');
    deleteCookie('client_access_gate');
    deleteCookie('client_role');
  },

  getToken: (): string | undefined => {
    return apiClient.getToken();
  },

  isAuthenticated: (): boolean => {
    const token = apiClient.getToken();
    return !!token && !isJwtExpired(token);
  },

  getCurrentUser: () => {
    const userStr = getCookie('auth_user');
    const userType = getCookie('auth_user_type');
    if (userStr && userType) {
      try {
        return {
          user: JSON.parse(decodeURIComponent(userStr)),
          userType: userType as 'user' | 'client'
        };
      } catch (error) {
        return null;
      }
    }
    return null;
  },
};

export default authApi;